import { SecureLoader } from "@ews-admin/global-design-system";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useDocumentLanguage } from "../../hooks/useDocumentLanguage";
import i18n from "../../i18n";
import {
  setLanguage,
  setSessionToken,
  validateSessionRequest,
} from "../../store/reducers/sessionSlice";
import { getSpecialtiesRequest } from "../../store/reducers/specialtiesSlice";
import { RootState, useAppDispatch } from "../../store/store";
import { storeMedecine360SessionToken } from "../../utils/session-utils";

// Supported languages
const SUPPORTED_LANGUAGES = ["fr", "en", "ar"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const isSupportedLanguage = (
  value: string | null | undefined
): value is SupportedLanguage =>
  value !== null &&
  value !== undefined &&
  SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);

/**
 * Reads the language from a language-prefixed path (`/fr`, `/en`, `/ar`).
 *
 * Those paths are shared with the public site, and this app has no route for
 * them: during maintenance every route is redirected to `/maintenance`, so the
 * prefix is the only signal of the language the reader asked for.
 */
const getLanguageFromPathname = (
  pathname: string
): SupportedLanguage | null => {
  const [firstSegment] = pathname.split("/").filter(Boolean);
  return isSupportedLanguage(firstSegment) ? firstSegment : null;
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { loading } = useSelector((state: RootState) => state.session);
  const hasInitialized = useRef(false);

  // Keep `<html lang>` / `<html dir>` in sync with the resolved language.
  useDocumentLanguage();

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Extract sessionToken and language from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get("sessionToken");
    const urlLanguage = urlParams.get("language");

    // Get language from the URL query, the URL path prefix, localStorage, or default to 'fr'
    const storedLanguage = localStorage.getItem("language");
    const language: SupportedLanguage =
      (isSupportedLanguage(urlLanguage) && urlLanguage) ||
      getLanguageFromPathname(window.location.pathname) ||
      (isSupportedLanguage(storedLanguage) && storedLanguage) ||
      "fr"; // Default to 'fr' if not provided or not supported

    if (sessionToken) {
      // Persist the token so early API calls can reliably read it
      storeMedecine360SessionToken(urlParams);
      dispatch(setSessionToken(sessionToken));
    }

    // Set i18n language and dispatch to Redux store
    const changeLanguage = async () => {
      try {
        await i18n.changeLanguage(language);
        // Persist language to localStorage
        localStorage.setItem("language", language);
      } catch (error) {
        console.warn("Failed to change i18n language:", error);
      }
    };

    changeLanguage();
    dispatch(setLanguage(language));

    // Remove sessionToken from the URL after consuming it.
    if (sessionToken) {
      urlParams.delete("sessionToken");
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }

    dispatch(validateSessionRequest());
    // Fetch specialties on app startup and when language changes
    dispatch(getSpecialtiesRequest());
  }, [dispatch]);

  if (loading) {
    return <SecureLoader label={t("common.loading")} />;
  }

  return <>{children}</>;
};
